import {
  CandlestickSeries,
  createChart,
  createSeriesMarkers,
  type IChartApi,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type SeriesMarker,
  type Time,
} from "lightweight-charts";
import { type MutableRefObject, type RefObject, useEffect, useRef } from "react";
import type { SignalDetailResponse, SignalView } from "../../lib/queries.js";
import { CHART_COLOR_PARSERS, readPalette } from "../chart/kit/chartPalette.js";
import { isChartDisposed, markChartDisposed } from "../chart/kit/useChart.js";

/**
 * The ±45 sessions around a signal, as candles.
 *
 * This is the one place candles earn their pixels. The symbol page's default
 * is five years at roughly 1,250 bars, where a candle body is a pixel wide;
 * ninety bars is where the open, high, low, and close are all legible.
 *
 * Colours resolve from the CSS custom properties through
 * `chart/kit/chartPalette.ts`, which owns the oklch conversion and the
 * `layout.colorParsers` entry the library needs to accept those tokens at all.
 * A canvas cannot take a `var()`, and hardcoding hex here would recreate the
 * exact second palette the kit exists to remove.
 *
 * Where there is no 2D context at all the chart stands down rather than
 * throwing, so the rest of the detail view still renders — in a browser with
 * canvas disabled, and under a jsdom that has not installed the shims from
 * `test-support/chartTestEnv.ts`.
 */

/**
 * lightweight-charts throws rather than degrades when there is no 2D context.
 *
 * Probed once: whether the document can paint a canvas does not change while
 * the page is open, and jsdom logs every probe.
 */
let canvasSupport: boolean | null = null;

function canDraw(doc: Document): boolean {
  if (canvasSupport === null) {
    try {
      canvasSupport = doc.createElement("canvas").getContext("2d") !== null;
    } catch {
      canvasSupport = false;
    }
  }
  return canvasSupport;
}

export function FocusChart({
  bars,
  nearby,
  highlightLabel,
  highlightSessionDate,
}: {
  bars: SignalDetailResponse["context"]["bars"];
  nearby: readonly SignalView[];
  /**
   * What to write against the marker: the signal's own title.
   *
   * Passed in rather than looked up. This used to search `nearby` by id, and
   * `nearby` is built by excluding the signal being viewed, so the lookup
   * could never hit and every marker read "this signal".
   */
  highlightLabel: string;
  /** The session the highlighted signal fired, which is where the eye should land. */
  highlightSessionDate: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  useFocusChartInstance(hostRef, chartRef, seriesRef);

  useEffect(() => {
    const series = seriesRef.current;
    // Captured, not read through the ref in the cleanup: the mount cleanup
    // nulls `chartRef.current` first, and `isChartDisposed(null)` is false.
    const chart = chartRef.current;
    if (!series || !chart) return;
    series.setData(
      bars.map((bar) => ({
        time: bar.sessionDate as Time,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
      })),
    );

    const palette = readPalette();
    // The signal being read is drawn above the bar and labelled; the others
    // sit below it as small dots, so the eye lands on the right one without
    // reading the axis.
    const markers: SeriesMarker<Time>[] = nearby.map((signal) => ({
      time: signal.sessionDate as Time,
      position: "belowBar",
      color: palette.muted,
      shape: "circle",
      text: "",
    }));
    markers.push({
      time: highlightSessionDate as Time,
      position: "aboveBar",
      color: palette.warning,
      shape: "arrowDown",
      text: highlightLabel,
    });
    markers.sort((a, b) => String(a.time).localeCompare(String(b.time)));
    // createSeriesMarkers attaches a plugin per call where setMarkers replaced
    // in place, so every data change would otherwise leave the last one behind.
    markersRef.current?.detach();
    markersRef.current = createSeriesMarkers(series, markers);
    chart.timeScale().fitContent();

    return () => {
      // Guarded: this passive cleanup runs after the mount effect's cleanup has
      // already called chart.remove(), and detaching invalidates a destroyed
      // model — which schedules a paint against a disposed canvas binding.
      if (!isChartDisposed(chart)) markersRef.current?.detach();
      markersRef.current = null;
    };
  }, [bars, nearby, highlightSessionDate, highlightLabel]);

  return <div ref={hostRef} className="w-full" data-testid="focus-chart" />;
}

/**
 * The chart and its candle series, for the component's whole lifetime.
 *
 * Its own hook so `FocusChart` stays inside the 80-line function limit, and so
 * the one place that calls `createChart` here also owns the teardown: the
 * disposal registration, the `remove()`, and the two refs it nulls.
 */
function useFocusChartInstance(
  hostRef: RefObject<HTMLDivElement | null>,
  chartRef: MutableRefObject<IChartApi | null>,
  seriesRef: MutableRefObject<ISeriesApi<"Candlestick"> | null>,
): void {
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !canDraw(host.ownerDocument)) return;

    const palette = readPalette();
    const chart = createChart(host, {
      height: 320,
      width: host.clientWidth || 800,
      layout: {
        background: { color: "transparent" },
        textColor: palette.muted,
        colorParsers: [...CHART_COLOR_PARSERS],
      },
      grid: { vertLines: { color: palette.border }, horzLines: { color: palette.border } },
      rightPriceScale: { borderColor: palette.border },
      timeScale: { borderColor: palette.border },
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: palette.up,
      downColor: palette.down,
      wickUpColor: palette.up,
      wickDownColor: palette.down,
      borderVisible: false,
    });

    chartRef.current = chart;
    seriesRef.current = series;
    return () => {
      // Registered before remove(), so the markers cleanup can tell a torn-down
      // chart from a live one. This chart comes from createChart rather than
      // from `useChart`, so nothing else puts it in that registry.
      markChartDisposed(chart);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [hostRef, chartRef, seriesRef]);
}
